<script lang="ts">
export default {
  name: "BaseMultiSelect",
};
</script>

<script setup lang="ts">
import Multiselect from "@vueform/multiselect";
import "@vueform/multiselect/themes/default.css";

export type MultiSelectOption = {
  label: string;
  value: string | number;
};

export type MultiSelectValue = string | number | MultiSelectOption;

export type Props = {
  name: string;
  options: MultiSelectOption[] | (() => Promise<MultiSelectOption[]>);
  modelValue?: MultiSelectValue | MultiSelectValue[];
  label: string;
  showLabel?: boolean;
  useExternalLabel?: boolean;
  placeholder?: string;
  helpMessage?: string;
  noOptionsText?: string;
  noResultsText?: string;
  searchable?: boolean;
  clearable?: boolean;
  returnObject?: boolean;
  multiple?: boolean;
  createOption?: boolean;
  loading?: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  v$?: any | null;
};

const { t } = useI18n();

const props = withDefaults(defineProps<Props>(), {
  modelValue: undefined,
  showLabel: true,
  useExternalLabel: false,
  placeholder: undefined,
  helpMessage: "",
  noOptionsText: "",
  noResultsText: "",
  searchable: false,
  clearable: false,
  returnObject: false,
  createOption: false,
  multiple: false,
  loading: false,
  v$: null,
});

const internalValue = ref(props.modelValue);

watch(
  () => props.modelValue,
  () => {
    internalValue.value = props.modelValue;
  }
);

interface MultiSelect extends Multiselect {
  refreshOptions(): void;
  $el: HTMLInputElement;
}

const multiSelectRef = ref<MultiSelect>();

const isValid = computed(() => {
  return !(props.v$ && props.v$.$error);
});

const isDirty = computed(() => props.v$ && props.v$.$dirty);

const handleBlurEvent = () => {
  if (props.v$) {
    props.v$.$touch();
  }
};

const handleChangeEvent = (value: MultiSelectOption) => {
  if (props.v$) {
    props.v$.$touch();
  }
  emit("update:modelValue", value);
  emit("change", value);
};

const handleSearchChangeEvent = (value: string) => {
  emit("update:searchChange", value);
};

const focus = () => {
  multiSelectRef.value?.$el.focus();
};

const refreshOptions = () => {
  multiSelectRef.value?.refreshOptions();
};

const noOptionsText = computed(
  () => props.noOptionsText || t("hints.noOptions")
);
const noResultsText = computed(
  () => props.noResultsText || t("hints.noResults")
);

defineExpose({
  refreshOptions,
  focus,
});

const emit = defineEmits([
  "update:modelValue",
  "update:searchChange",
  "change",
]);
</script>

<template>
  <div
    class="mb-3"
    :class="{
      'has-validation': isDirty,
    }"
  >
    <label
      v-if="!useExternalLabel"
      :class="{
        'form-label': true,
        'visually-hidden': !showLabel,
      }"
      :for="name"
    >
      {{ label }}
    </label>
    <Multiselect
      :id="name"
      ref="multiSelectRef"
      v-model="internalValue"
      :mode="multiple ? 'tags' : 'single'"
      :close-on-select="!multiple"
      :placeholder="placeholder"
      :no-options-text="noOptionsText"
      :no-results-text="noResultsText"
      :searchable="searchable"
      :create-option="createOption"
      :can-clear="clearable"
      :object="returnObject"
      :delay="typeof options === 'function' ? 0 : -1"
      :multiple="multiple"
      :class="[{ 'is-invalid': !isValid && isDirty }]"
      :min-chars="1"
      :options="options"
      :native-support="true"
      :loading="loading"
      @blur="handleBlurEvent"
      @change="handleChangeEvent"
      @search-change="handleSearchChangeEvent"
    />
    <div v-if="isValid && helpMessage" class="form-text">{{ helpMessage }}</div>
    <div v-if="!isValid" class="invalid-feedback d-flex">
      {{ v$.$errors[0].$message }}
    </div>
  </div>
</template>

<style scoped lang="scss">
.multiselect {
  --ms-tag-bg: #{$brand-tools-subtle};
  --ms-option-bg-selected: #{$brand-tools-subtle};
  --ms-option-bg-selected-pointed: #{$brand-tools-subtle};
  --ms-border-color: #{$color-gray-300};
  --ms-tag-color: #{$primary-dark};
  --ms-option-color-selected-pointed: #{$body-color};
  --ms-option-color-selected: #{$body-color};
  --ms-line-height: 1.5;
  --ms-tag-font-weight: 400;
  --ms-py: 0.375rem;
  --ms-px: 0.75rem;

  &.is-invalid {
    border-color: $color-red;
  }

  &.is-open,
  &.is-active,
  &:focus {
    border-color: #a8aec4 !important;
    outline: 0 !important;
    box-shadow: 0 0 0 0.25rem rgb(80 92 136 / 25%) !important;
  }

  .multiselect-wrapper {
    min-height: 0 !important;
  }

  .multiselect-dropdown {
    overflow: auto;

    .multiselect-options .multiselect-option {
      &:hover:not(.is-selected),
      &.is-pointed:not(.is-selected) {
        background-color: rgb(233 236 239);
        color: $color-black;
      }
    }
  }
}
</style>
